import React, { useState, useEffect } from "react";
import { storage } from "../config/firebaseConfig";
import { ref, uploadBytes } from "firebase/storage";
import axios from "axios";
import { debugLog } from "../utils/debug";
import { API_BASE_URL } from "../config/environment"; // Import from environment config
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

export default function UploadAndProcess() {
  const [file, setFile] = useState(null);
  const [csvUrl, setCsvUrl] = useState("");
  const [, setMd5Hash] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [partialExists, setPartialExists] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [competitionsLoading, setCompetitionsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load competitions from API
  const loadCompetitions = async () => {
    try {
      setCompetitionsLoading(true);
      const apiUrl = `${API_BASE_URL}/competitions`;
      
      const response = await axios.get(apiUrl);
      setCompetitions(response.data);
    } catch (err) {
      console.error('Error loading competitions:', err);
      setError('Failed to load competitions');
    } finally {
      setCompetitionsLoading(false);
    }
  };

  useEffect(() => {
    loadCompetitions();
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setCsvUrl("");
    }
  };

  const handleUploadAndProcess = async () => {
    if (!file) {
      alert("Please select a file.");
      return;
    }
    setPartialExists(false);
    setIsLoading(true);

    const storageRef = ref(storage, `uploads/${file.name}`);
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const metadata = snapshot.metadata;
      debugLog("File uploaded:", metadata.fullPath);
      debugLog("MD5 Hash:", metadata.md5Hash);
      setMd5Hash(metadata.md5Hash);

      // Use the correct API URL based on environment
      const functionUrl = `${API_BASE_URL}/process-file`;
      
      const response = await axios.post(
        functionUrl,
        { md5Hash: metadata.md5Hash, filePath: metadata.fullPath },
        { headers: { "Content-Type": "application/json" } }
      );

      setPartialExists(response.data.partialMatch);
      const csvData = response.data.csvData;
      const csvFilePath = response.data.csvFilePath;
      const resultsCount = response.data.resultsCount;
      
      // Create blob for download
      const blob = new Blob([csvData], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      setCsvUrl(downloadUrl);

      // Save results to Firestore if competition is selected
      if (selectedCompetition) {
        try {
          // Parse CSV to get results data
          const lines = csvData.split('\n');
          const headers = lines[0].split(',');
          const results = lines.slice(1).filter(line => line.trim()).map(line => {
            const values = line.split(',');
            const result = {};
            headers.forEach((header, index) => {
              result[header.trim()] = values[index] ? values[index].trim() : '';
            });
            return result;
          });

          // Save to Firestore with CSV file information
          const saveResponse = await axios.post(
            `${API_BASE_URL}/competition-results`,
            {
              competitionId: selectedCompetition,
              results: results,
              fileName: file.name,
              filePath: metadata.fullPath,
              csvFilePath: csvFilePath,
              resultsCount: resultsCount
            }
          );

          debugLog('Results saved to Firestore:', saveResponse.data);
        } catch (saveError) {
          console.error('Error saving results to Firestore:', saveError);
          // Don't fail the upload if saving to Firestore fails
        }
      }
    } catch (error) {
      console.error("Upload and processing error:", error);
      
      // Display error message to user
      let errorMessage = "An error occurred during upload and processing.";
      if (error.response && error.response.data) {
        // If the error response contains text data, use it
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data instanceof Blob) {
          // Handle blob response (convert to text)
          const text = await error.response.data.text();
          errorMessage = text || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center', // Center content vertically
        // minHeight: 'calc(100vh - 64px - 100px - 44px)', // Adjust for Toolbar and Footer
        p: 4,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          width: "400px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Upload and Process Results
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}

        {/* Competition Selection */}
        <FormControl fullWidth sx={{ minWidth: 200 }}>
          <InputLabel>Select Competition</InputLabel>
          <Select
            value={selectedCompetition}
            label="Select Competition"
            onChange={(e) => setSelectedCompetition(e.target.value)}
            disabled={competitionsLoading || isLoading}
          >
            <MenuItem value="">
              <em>Choose a competition...</em>
            </MenuItem>
            {competitions.map((competition) => {
              let dateStr = 'Invalid Date';
              try {
                if (competition.date) {
                  if (competition.date.toDate && typeof competition.date.toDate === 'function') {
                    // Firestore Timestamp
                    dateStr = new Date(competition.date.toDate()).toLocaleDateString();
                  } else if (competition.date._seconds !== undefined && competition.date._nanoseconds !== undefined) {
                    // Raw Firestore Timestamp object - create date at start of day in local timezone
                    const utcDate = new Date(competition.date._seconds * 1000);
                    const localDate = new Date(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate());
                    dateStr = localDate.toLocaleDateString();
                  } else if (competition.date instanceof Date) {
                    // JavaScript Date object
                    dateStr = competition.date.toLocaleDateString();
                  } else if (typeof competition.date === 'string') {
                    // String date - try to parse it
                    const parsedDate = new Date(competition.date);
                    if (!isNaN(parsedDate.getTime())) {
                      dateStr = parsedDate.toLocaleDateString();
                    }
                  }
                }
              } catch (error) {
                console.error('Error parsing competition date:', error);
              }
              
              return (
                <MenuItem key={competition.id} value={competition.id}>
                  {competition.name} - {dateStr}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <input
          style={{ display: "none" }}
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
          disabled={isLoading}
        />
        <label htmlFor="contained-button-file">
          <Button
            variant="contained"
            component="span"
            startIcon={<UploadFileIcon />}
            disabled={isLoading}
          >
            Choose File
          </Button>
        </label>

        <Typography variant="body2">
          {file ? file.name : "No file chosen"}
        </Typography>

        <Button
          variant="contained"
          onClick={handleUploadAndProcess}
          disabled={isLoading || !selectedCompetition || !file}
          sx={{ textTransform: 'uppercase' }} // Match the "SEARCH" button style
        >
          {isLoading ? <CircularProgress size={24} /> : "Upload & Process"}
        </Button>

        {!selectedCompetition && (
          <Typography variant="body2" color="warning.main">
            Please select a competition before uploading
          </Typography>
        )}

        {csvUrl && (
          <Typography
            variant="body1"
            sx={{
              color: isLoading ? "grey" : "inherit",
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            CSV ready for download:{" "}
            <a
              href={csvUrl}
              download="results.csv"
              style={{
                color: isLoading ? "grey" : "inherit",
                textDecoration: isLoading ? "none" : "underline",
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            >
              Download CSV
            </a>
          </Typography>
        )}
        {partialExists && (
          <Typography variant="body2" color="warning.main">
            Partial matches exist. Please download the CSV to view.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}